import React, { useState, useRef, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { Upload, X, Loader, Image as ImageIcon, Check, ZoomIn, ZoomOut } from 'lucide-react';
import { supabase } from '../../supabaseClient';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const MAX_SIZE = 10 * 1024 * 1024;

export async function getCroppedBlob(imageSrc, pixelCrop) {
  const image = new Image();
  image.crossOrigin = 'anonymous';
  image.src = imageSrc;
  await new Promise((res, rej) => { image.onload = res; image.onerror = rej; });
  const canvas = document.createElement('canvas');
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  canvas.getContext('2d').drawImage(
    image,
    pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
    0, 0, pixelCrop.width, pixelCrop.height,
  );
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.88));
}

export async function uploadFile(file) {
  const { data: { user } } = await supabase.auth.getUser();
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch(`${API_URL}/api/upload`, {
    method: 'POST',
    headers: user ? { 'x-user-id': user.id } : {},
    body: formData,
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Upload failed');
  return json.url;
}

function ImageUploader({ onUpload, aspect = 16 / 9, className = '', crop = true, multiple = false }) {
  const [rawSrc, setRawSrc] = useState(null);
  const [crop_, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const onCropComplete = useCallback((_, pixels) => setCroppedAreaPixels(pixels), []);

  const loadFile = (file) => {
    if (!file) return;
    setError(null);
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file.');
      return;
    }
    if (file.size > MAX_SIZE) {
      setError('Image must be under 10MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      setRawSrc(e.target.result);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setPreview(null);
    };
    reader.readAsDataURL(file);
  };

  // No-crop path: uploads one or more files as-is (original aspect ratio,
  // original format) instead of forcing every photo through the 16:9 cover
  // cropper — right for day-by-day travel photos, which are rarely 16:9.
  const loadFilesUncropped = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith('image/'));
    if (files.length === 0) {
      setError('Please upload an image file.');
      return;
    }
    const oversized = files.find((f) => f.size > MAX_SIZE);
    if (oversized) {
      setError('Each image must be under 10MB.');
      return;
    }
    setError(null);
    setUploading(true);
    setUploadingCount(files.length);
    try {
      for (const file of files) {
        const url = await uploadFile(file);
        onUpload(url);
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
      setUploadingCount(0);
    }
  };

  const handleConfirmCrop = async () => {
    if (!rawSrc || !croppedAreaPixels) return;
    setError(null);
    setUploading(true);
    try {
      const blob = await getCroppedBlob(rawSrc, croppedAreaPixels);
      setPreview(URL.createObjectURL(blob));
      setRawSrc(null);

      const url = await uploadFile(new File([blob], 'cover.jpg', { type: 'image/jpeg' }));
      onUpload(url);
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Upload failed. Please try again.');
      setRawSrc(null);
      setPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const reset = () => {
    setRawSrc(null);
    setPreview(null);
    setError(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  /* ── No-crop UI: upload as-is, one or many files ── */
  if (!crop) {
    return (
      <div className={className}>
        <div
          onClick={() => inputRef.current?.click()}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); loadFilesUncropped(e.dataTransfer.files); }}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
            dragOver
              ? 'border-coral-400 bg-coral-50'
              : 'border-platinum-300 hover:border-coral-300 bg-platinum-50 hover:bg-coral-50/30'
          }`}
        >
          <div className="flex flex-col items-center gap-2">
            {uploading
              ? <Loader size={28} className="text-coral-400 animate-spin" />
              : dragOver ? <ImageIcon size={28} className="text-coral-400" /> : <Upload size={28} className="text-platinum-500" />
            }
            <p className="text-sm text-charcoal-400">
              {uploading
                ? `Uploading${uploadingCount > 1 ? ` ${uploadingCount} photos` : ''}…`
                : dragOver ? 'Drop photo(s) here' : multiple ? 'Click or drag photos — multiple OK' : 'Click or drag a photo'}
            </p>
            <p className="text-xs text-platinum-500">Any image format — max 10MB each, kept at original size</p>
          </div>
        </div>
        {error && <p className="text-sm text-red-500 mt-2">{error}</p>}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple={multiple}
          onChange={(e) => loadFilesUncropped(e.target.files)}
          className="hidden"
        />
      </div>
    );
  }

  /* ── Cropper UI ── */
  if (rawSrc) {
    return (
      <div className={`${className} space-y-3`}>
        <div className="relative w-full rounded-xl overflow-hidden border border-platinum-200 bg-charcoal-600" style={{ height: 280 }}>
          <Cropper
            image={rawSrc}
            crop={crop_}
            zoom={zoom}
            aspect={aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>
        <div className="flex items-center gap-3 px-1">
          <ZoomOut size={15} className="text-platinum-500 flex-shrink-0" />
          <input
            type="range" min={1} max={3} step={0.05} value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="flex-1 accent-coral-500"
          />
          <ZoomIn size={15} className="text-platinum-500 flex-shrink-0" />
        </div>
        <div className="flex gap-2">
          <button
            onClick={reset}
            className="flex-1 py-2 rounded-xl border border-platinum-200 text-sm text-charcoal-500 hover:bg-platinum-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirmCrop}
            disabled={uploading}
            className="flex-1 py-2 rounded-xl bg-coral-500 hover:bg-coral-600 text-white text-sm font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
          >
            {uploading ? <Loader size={15} className="animate-spin" /> : <Check size={15} />}
            {uploading ? 'Uploading…' : 'Use Photo'}
          </button>
        </div>
        {error && <p className="text-sm text-red-500">{error}</p>}
      </div>
    );
  }

  /* ── Preview after upload ── */
  if (preview) {
    return (
      <div className={`${className} relative rounded-xl overflow-hidden border border-platinum-200`}>
        <img src={preview} alt="Preview" className="w-full h-48 object-cover" />
        {uploading && (
          <div className="absolute inset-0 bg-charcoal-500/50 flex items-center justify-center">
            <Loader className="animate-spin text-white" size={32} />
          </div>
        )}
        {!uploading && (
          <button onClick={reset} className="absolute top-2 right-2 w-8 h-8 bg-charcoal-500/70 hover:bg-charcoal-500 rounded-full flex items-center justify-center text-white transition-colors">
            <X size={16} />
          </button>
        )}
      </div>
    );
  }

  /* ── Drop zone ── */
  return (
    <div className={className}>
      <div
        onClick={() => inputRef.current?.click()}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); loadFile(e.dataTransfer.files[0]); }}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
          dragOver
            ? 'border-coral-400 bg-coral-50'
            : 'border-platinum-300 hover:border-coral-300 bg-platinum-50 hover:bg-coral-50/30'
        }`}
      >
        <div className="flex flex-col items-center gap-2">
          {dragOver
            ? <ImageIcon size={28} className="text-coral-400" />
            : <Upload size={28} className="text-platinum-500" />
          }
          <p className="text-sm text-charcoal-400">{dragOver ? 'Drop image here' : 'Click or drag an image'}</p>
          <p className="text-xs text-platinum-500">Any image format — max 10MB</p>
        </div>
      </div>
      {error && <p className="text-sm text-red-500 mt-2">{error}</p>}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={(e) => loadFile(e.target.files[0])}
        className="hidden"
      />
    </div>
  );
}

export default ImageUploader;
